import { Button, Form, Select, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import {
    DatePicker as ZhDatePicker,
    DetailHeader as ZhDetailHeader,
    InputNumber as ZhInputNumber,
    Table as ZhTable,
    Upload as ZhUpload,
    type TableColumnProps,
    type TableRef,
    type UploadRequestOptions,
    type UploadUserFile
} from "zhihao-ui-react";

import request from "@/api";
import { orderService } from "@/api/services/orderService";
import { receivePaymentService } from "@/api/services/receivePaymentService";
import { RECEIVE_PAYMENT_TYPE, TRADE_ORDER_STATE } from "@zhihaoscm/meta";
import type { InvoiceForm, OrderVo, ReceivePayment } from "@zhihaoscm/entity";
import { dateFormat } from "@/utils";
import { fmtMoney } from "@/utils/money";
import RequiredMark from "@/views/components/RequiredMark";
import "./TradeInvoiceFormPage.scss";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

type InvoicePayment = ReceivePayment & { invoicedAmount: number };
type InvoiceFieldErrors = Partial<Record<"orderId" | "receivePayments" | "invoiceDate" | "fileId", string>>;

export interface TradeInvoiceFormPageProps {
    addInvoice?: (form: InvoiceForm) => Promise<unknown>;
    loadOrderOptions?: (keyword?: string) => Promise<OrderVo[]>;
    loadReceivePayments?: (orderId: string) => Promise<ReceivePayment[]>;
}

/** 复用 Vue 发票新增接口，保留发票表单的核心提交契约。 */
export async function addTradeInvoice(form: InvoiceForm): Promise<unknown> {
    const response = await request.service.post("/admin/invoice", form);
    return response.data;
}

/** 查询可开票订单，参数与旧版 remote-select 完全一致。 */
export async function loadTradeInvoiceOrderOptions(keyword = ""): Promise<OrderVo[]> {
    const result = await orderService.orderSelector({
        page: 1,
        size: 50,
        keyword: keyword.trim(),
        states: [
            TRADE_ORDER_STATE.TO_BE_PICKED_UP.code,
            TRADE_ORDER_STATE.IN_THE_PROCESS_OF_PICKING_UP_THE_GOODS.code,
            TRADE_ORDER_STATE.COMPLETED.code,
            TRADE_ORDER_STATE.CANCELLED.code
        ],
        isFilterUninvoicedAndReceived: 1
    });
    return result.list || [];
}

/** 查询订单下未开票且已收款的收款单。 */
export async function loadTradeInvoiceReceivePayments(orderId: string): Promise<ReceivePayment[]> {
    const result = await receivePaymentService.uninvoicedByOrderId({ orderId });
    return Array.isArray(result) ? result : [];
}

const INVOICE_FILE_MAX_SIZE = 10 * 1024 * 1024;
const INVOICE_FILE_EXTENSIONS = new Set(["pdf", "ofd", "jpeg", "jpg", "png", "xml", "xls", "xlsx"]);
const INVOICE_FILE_TYPES = new Set([
    "application/pdf",
    "application/ofd",
    "image/jpeg",
    "image/png",
    "application/xml",
    "text/xml",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
]);

/** 保留 Vue 发票规则：今天之后的开票日期不可选。 */
function disableInvoiceDateAfterToday(date: Date): boolean {
    return date.getTime() > Date.now();
}

/** 组合旧版订单下拉展示文本：采购用户、手机号、吨位、订单号和日期。 */
function orderOptionLabel(item: OrderVo): string {
    const order = item.order;
    const customer = order.customerEnterprise;
    const name = customer?.name || customer?.realName;
    const mobile = customer?.mobile || "";
    return `${name ? `${name}|` : ""}${mobile}|${order.ton ?? ""}|${order.id}|${dateFormat(order.createdTime)}`;
}

/** 发票新增页，按 Vue 旧版恢复订单远程搜索、收款明细选择和提交流程。 */
export function TradeInvoiceFormPage({
    addInvoice = addTradeInvoice,
    loadOrderOptions = loadTradeInvoiceOrderOptions,
    loadReceivePayments = loadTradeInvoiceReceivePayments
}: TradeInvoiceFormPageProps) {
    const navigate = useNavigate();
    const [orderOptions, setOrderOptions] = useState<OrderVo[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<OrderVo | null>(null);
    const [payments, setPayments] = useState<InvoicePayment[]>([]);
    const [selectedPaymentIds, setSelectedPaymentIds] = useState<string[]>([]);
    const [invoiceDate, setInvoiceDate] = useState("");
    const [invoiceFileId, setInvoiceFileId] = useState<number | null>(null);
    const [invoiceFileList, setInvoiceFileList] = useState<UploadUserFile[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<InvoiceFieldErrors>({});
    const [orderLoading, setOrderLoading] = useState(false);
    const paymentTableRef = useRef<TableRef<InvoicePayment> | null>(null);

    useEffect(() => {
        if (payments.length) {
            // Vue 的 zh-table 在收款单加载后调用 toggleAllSelection，保持选中态和金额合计同步。
            paymentTableRef.current?.toggleAllSelection();
        }
    }, [payments]);

    /** 执行 Vue remote-select 查询，展开和输入均支持请求接口。 */
    const searchOrderOptions = async (keyword = "") => {
        setOrderLoading(true);
        try {
            setOrderOptions((await loadOrderOptions(keyword)) || []);
            setErrorMessage(null);
        } catch {
            setErrorMessage("订单选项加载失败");
        } finally {
            setOrderLoading(false);
        }
    };

    /** 选中订单后查询可开票收款单，并把未开票金额作为默认开票金额。 */
    const handleOrderChange = async (orderId: string) => {
        const option = orderOptions.find(item => String(item.order.id) === String(orderId));
        setSelectedOrder(option || null);
        setPayments([]);
        setSelectedPaymentIds([]);
        setFieldErrors(current => ({ ...current, orderId: undefined, receivePayments: undefined }));
        if (!option) return;
        try {
            const result = await loadReceivePayments(String(option.order.id));
            const loadedPayments = (result || []).map(item => ({
                ...item,
                invoicedAmount: Number(item.uninvoicedAmount) || 0
            }));
            setPayments(loadedPayments);
            // Vue 的表格在收款单加载后默认全选，新增页面按相同行为计算开票总额。
            setSelectedPaymentIds(loadedPayments.map(item => String(item.id)));
            setErrorMessage(null);
        } catch {
            setErrorMessage("收款单加载失败");
        }
    };

    /** 更新收款单的开票金额，保留其他收款单的编辑值。 */
    const updatePaymentAmount = (id: string | number, value: number | null) => {
        setPayments(current =>
            current.map(item =>
                String(item.id) === String(id) ? { ...item, invoicedAmount: Number(value) || 0 } : item
            )
        );
    };

    /** 校验发票单据格式及大小，规则与旧版 SingleUpload 对齐。 */
    const validateInvoiceFile = (file: File): boolean => {
        const extension = file.name.split(".").pop()?.toLowerCase() || "";
        if (!INVOICE_FILE_EXTENSIONS.has(extension) || (file.type && !INVOICE_FILE_TYPES.has(file.type))) {
            setFieldErrors(current => ({ ...current, fileId: "发票单据仅支持 PDF、OFD、JPEG、PNG、XML、Excel 格式" }));
            return false;
        }
        if (file.size > INVOICE_FILE_MAX_SIZE) {
            setFieldErrors(current => ({ ...current, fileId: "发票单据大小不能超过10MB" }));
            return false;
        }
        return true;
    };

    /** 上传发票单据并回填后端需要的文件 ID。 */
    const uploadInvoiceFile = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        try {
            const formData = new FormData();
            formData.append("file", file as File);
            formData.append("fileName", (file as File).name);
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            const fileId = Number(uploadedFile.id);
            if (!Number.isInteger(fileId) || fileId <= 0) throw new Error("invalid file id");
            setInvoiceFileId(fileId);
            setInvoiceFileList([
                { name: uploadedFile.name, response: uploadedFile, status: "success", url: uploadedFile.path }
            ]);
            setErrorMessage(null);
            setFieldErrors(current => ({ ...current, fileId: undefined }));
            onSuccess?.(uploadedFile);
        } catch {
            setFieldErrors(current => ({ ...current, fileId: "发票单据上传失败" }));
            onError?.(new Error("发票单据上传失败"));
        }
    };

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const selectedPayments = payments.filter(item => selectedPaymentIds.includes(String(item.id)));
        const errors: InvoiceFieldErrors = {};
        if (!selectedOrder?.order.id) errors.orderId = "请选择关联订单";
        if (!selectedPayments.length) errors.receivePayments = "请选择关联订单下的收款单";
        else if (selectedPayments.some(item => item.invoicedAmount <= 0)) {
            errors.receivePayments = "请填写所有选中项的开票金额";
        } else if (selectedPayments.some(item => item.invoicedAmount > Number(item.uninvoicedAmount))) {
            errors.receivePayments = "开票金额不能大于未开票金额";
        }
        if (!invoiceDate) errors.invoiceDate = "请选择开票日期";
        if (!invoiceFileId) errors.fileId = "请上传单据";
        setFieldErrors(errors);
        if (Object.keys(errors).length || !selectedOrder?.order.id || !invoiceFileId) {
            return;
        }
        const relatedReceiveInfo = selectedPayments.map(item => ({
            receivePaymentId: item.id,
            invoiceAmount: item.invoicedAmount
        }));
        const payload: InvoiceForm = {
            orderId: String(selectedOrder.order.id),
            relatedReceiveInfo,
            amount: relatedReceiveInfo.reduce((total, item) => total + item.invoiceAmount, 0),
            invoiceDate,
            fileId: invoiceFileId
        };
        try {
            await addInvoice(payload);
            setErrorMessage(null);
            notifyOperationSuccess("新增成功");
            navigate(-1);
        } catch {
            setErrorMessage("发票新增失败");
        }
    };

    const paymentColumns: TableColumnProps<InvoicePayment>[] = [
        { type: "selection", width: 48 },
        {
            label: "收款编号",
            width: 276,
            prop: "id",
            render: ({ row }) => <Link to={`/tradeManage/collection/detail?id=${row.id}`}>{row.id}</Link>
        },
        {
            label: "费用类型",
            width: 158,
            prop: "paymentType",
            render: ({ row }) => RECEIVE_PAYMENT_TYPE.getIntroByCode(row.paymentType) || "-"
        },
        { label: "支付金额(元)", width: 147, prop: "amount", render: ({ row }) => fmtMoney(row.amount, "元") },
        {
            label: "支付时间",
            width: 147,
            prop: "payEndTime",
            render: ({ row }) => dateFormat(row.payTime || row.payEndTime)
        },
        {
            label: "开票金额",
            width: 299,
            prop: "invoicedAmount",
            renderHeader: () => (
                <div>
                    <RequiredMark />
                    开票金额
                </div>
            ),
            render: ({ row }) => (
                <ZhInputNumber
                    aria-label={`开票金额-${row.id}`}
                    min={0}
                    value={row.invoicedAmount}
                    suffix="元"
                    onChange={value => updatePaymentAmount(row.id, typeof value === "number" ? value : Number(value))}
                />
            )
        }
    ];

    return (
        <section aria-label="trade-invoice-form-page" className="trade-invoice-form-page">
            <section className="section-card trade-invoice-form__header">
                <ZhDetailHeader title="新增发票" />
            </section>
            <section className="section-card trade-invoice-form__section">
                <div className="section-title">关联信息</div>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <form onSubmit={event => void submit(event)}>
                    <Form.Item
                        className="trade-invoice-form__form-item"
                        validateStatus={fieldErrors.orderId ? "error" : undefined}
                        help={fieldErrors.orderId}
                    >
                        <div className="trade-invoice-form__row">
                            <label className="trade-invoice-form__label" htmlFor="trade-invoice-order">
                                <RequiredMark />
                                关联订单
                            </label>
                            <Select
                                id="trade-invoice-order"
                                aria-label="关联订单"
                                allowClear
                                filterOption={false}
                                loading={orderLoading}
                                options={orderOptions.map(item => ({
                                    label: orderOptionLabel(item),
                                    value: item.order.id
                                }))}
                                /* 旧版 el-select 的下拉层按订单文本展开，不能被输入框宽度截断。 */
                                popupMatchSelectWidth={545}
                                classNames={{ popup: { root: "trade-invoice-order-dropdown" } }}
                                placeholder="请选择关联订单"
                                showSearch
                                onChange={value => void handleOrderChange(String(value || ""))}
                                onOpenChange={open => {
                                    if (open) void searchOrderOptions();
                                }}
                                onSearch={value => void searchOrderOptions(value)}
                            />
                        </div>
                    </Form.Item>
                    {selectedOrder ? (
                        <div className="trade-invoice-form__order-detail">
                            {/* 使用固定三列避免长标签换行，保持 Vue detail-json 的横向字段布局。 */}
                            <div className="trade-invoice-form__order-detail-item">
                                <span>采购账号</span>
                                <span>{`${selectedOrder.order.customerEnterprise?.realName || "-"} (${selectedOrder.order.customerEnterprise?.mobile || "-"})`}</span>
                            </div>
                            <div className="trade-invoice-form__order-detail-item">
                                <span>实际收款金额</span>
                                <span>{fmtMoney(selectedOrder.order.actualReceivedAmount, "元")}</span>
                            </div>
                            <div className="trade-invoice-form__order-detail-item">
                                <span>未开票金额</span>
                                <span>{fmtMoney(selectedOrder.order.uninvoicedAmount, "元")}</span>
                            </div>
                        </div>
                    ) : null}
                    {payments.length ? (
                        <div className="trade-invoice-form__payment-table">
                            <ZhTable<InvoicePayment>
                                ref={paymentTableRef}
                                border
                                columns={paymentColumns}
                                data={payments}
                                pagination={false}
                                rowKey={row => String(row.id)}
                                onSelectionChange={rows => setSelectedPaymentIds(rows.map(row => String(row.id)))}
                            />
                            <div className="trade-invoice-form__total">
                                <span>开票总金额</span>
                                <span>
                                    {fmtMoney(
                                        payments
                                            .filter(item => selectedPaymentIds.includes(String(item.id)))
                                            .reduce((total, item) => total + item.invoicedAmount, 0),
                                        "元"
                                    )}
                                </span>
                            </div>
                            {fieldErrors.receivePayments ? (
                                <div className="trade-invoice-form__field-error" role="alert">
                                    {fieldErrors.receivePayments}
                                </div>
                            ) : null}
                        </div>
                    ) : fieldErrors.receivePayments ? (
                        <div className="trade-invoice-form__field-error" role="alert">
                            {fieldErrors.receivePayments}
                        </div>
                    ) : null}
                    <div className="trade-invoice-form__bottom-row">
                        <Form.Item
                            className="trade-invoice-form__form-item"
                            validateStatus={fieldErrors.fileId ? "error" : undefined}
                            help={fieldErrors.fileId}
                        >
                            <div className="trade-invoice-form__row">
                                <label className="trade-invoice-form__label" htmlFor="trade-invoice-file">
                                    <RequiredMark />
                                    发票单据
                                </label>
                                <div className="trade-invoice-upload">
                                    <ZhUpload
                                        action="#"
                                        aria-label="发票单据"
                                        accept=".pdf,.ofd,.jpeg,.jpg,.png,.xml,.xls,.xlsx,application/pdf,image/jpeg,image/png,application/xml"
                                        className="admin-upload-file-list"
                                        fileList={invoiceFileList}
                                        limit={1}
                                        onPreview={file => {
                                            if (file.url) window.open(file.url, "_blank", "noopener,noreferrer");
                                        }}
                                        onRemove={() => {
                                            setInvoiceFileId(null);
                                            setInvoiceFileList([]);
                                            setFieldErrors(current => ({ ...current, fileId: undefined }));
                                        }}
                                        showFileList
                                        beforeUpload={validateInvoiceFile}
                                        httpRequest={uploadInvoiceFile}
                                    >
                                        <Button htmlType="button" type="link">
                                            <span className="trade-invoice-upload__label">
                                                <i aria-hidden="true" className="iconfont icon-Frame-14 pr-5" />
                                                <span className="trade-invoice-upload__text">上传</span>
                                            </span>
                                        </Button>
                                    </ZhUpload>
                                </div>
                            </div>
                        </Form.Item>
                        <Form.Item
                            className="trade-invoice-form__form-item"
                            validateStatus={fieldErrors.invoiceDate ? "error" : undefined}
                            help={fieldErrors.invoiceDate}
                        >
                            <div className="trade-invoice-form__row">
                                <label className="trade-invoice-form__label" htmlFor="trade-invoice-date">
                                    <RequiredMark />
                                    开票日期
                                </label>
                                <ZhDatePicker
                                    id="trade-invoice-date"
                                    aria-label="开票日期"
                                    format="YYYY-MM-DD"
                                    placeholder="请选择开票日期"
                                    type="date"
                                    value={invoiceDate}
                                    valueFormat="YYYY-MM-DD"
                                    disabledDate={disableInvoiceDateAfterToday}
                                    onChange={(_value, formattedValue) => {
                                        setInvoiceDate(typeof formattedValue === "string" ? formattedValue : "");
                                        setFieldErrors(current => ({ ...current, invoiceDate: undefined }));
                                    }}
                                />
                            </div>
                        </Form.Item>
                    </div>
                    <Space className="operation-fixed-btn" size={0}>
                        <Button htmlType="submit">提交</Button>
                        <Button htmlType="button" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </form>
            </section>
        </section>
    );
}

export default TradeInvoiceFormPage;
